import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Stack,
  Text,
  Dropdown,
  IDropdownOption,
  IconButton,
  ComboBox,
  IComboBoxOption,
} from "@fluentui/react";
import { DataGrid, DataGridColumn } from "@/ui-controls/DataGrid";
import MoreActionsButton from "@/ui-controls/MoreActionsButton";
import ShimmerList from "@/ui-controls/ShimmerList";
import RigthPanel, { RigthPanelType } from "@/ui-controls/RigthPanel";
import AuditDetailsPanel from "@/components/integrations/audits/AuditDetailsPanel";
import {
  IntegrationAuditDto,
  getIntegrationAudits,
} from "@/api/integrations";
import { User, getAllActiveUsers } from "@/api/users";

const entityTypeOptions: IDropdownOption[] = [
  { key: "", text: "All" },
  { key: "Integration", text: "Integration" },
  { key: "IntegrationConfiguration", text: "Configuration" },
  { key: "CustomerIntegrationSetting", text: "Customer Setting" },
  { key: "IntegrationDefaultPricing", text: "Default Pricing" },
  { key: "CustomerIntegrationPricing", text: "Customer Pricing" },
];

const IntegrationAuditsPage = () => {
  const [entityTypeFilter, setEntityTypeFilter] = useState<string>("");
  const [userIdFilter, setUserIdFilter] = useState<number | undefined>(
    undefined
  );
  const [audits, setAudits] = useState<IntegrationAuditDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedAudit, setSelectedAudit] =
    useState<IntegrationAuditDto | null>(null);
  const [users, setUsers] = useState<User[]>([]);

  useEffect(() => {
    getAllActiveUsers().then((data) => setUsers(data || []));
  }, []);

  const fetchAudits = useCallback(() => {
    setLoading(true);
    getIntegrationAudits({
      entityType: entityTypeFilter || undefined,
      pageNumber: 1,
      pageSize: 1000,
    })
      .then((data) => setAudits(data || []))
      .catch(() => setAudits([]))
      .finally(() => setLoading(false));
  }, [entityTypeFilter]);

  useEffect(() => {
    fetchAudits();
  }, [fetchAudits]);

  const userOptions: IComboBoxOption[] = useMemo(() => {
    const distinctIds = [...new Set(audits.map((a) => a.userId).filter(Boolean))];
    return distinctIds.map((id) => {
      const user = users.find((u) => u.Id === id);
      return { key: id!, text: user ? user.EmailAddress : id!.toString() };
    });
  }, [audits, users]);

  const filteredAudits = useMemo(() => {
    if (userIdFilter === undefined) return audits;
    return audits.filter((a) => a.userId === userIdFilter);
  }, [audits, userIdFilter]);

  const columns: DataGridColumn[] = [
    {
      key: "dateModifiedUtc",
      name: "Date",
      minWidth: 160,
      maxWidth: 200,
      onRender: (item: IntegrationAuditDto) =>
        new Date(item.dateModifiedUtc).toLocaleString(),
    },
    {
      key: "entityType",
      name: "Entity Type",
      fieldName: "entityType",
      minWidth: 150,
      maxWidth: 200,
    },
    {
      key: "entityId",
      name: "Entity ID",
      fieldName: "entityId",
      minWidth: 80,
      maxWidth: 100,
    },
    {
      key: "userId",
      name: "User",
      minWidth: 150,
      maxWidth: 220,
      onRender: (item: IntegrationAuditDto) => {
        const user = users.find((u) => u.Id === item.userId);
        return user ? user.EmailAddress : item.userId?.toString() ?? "-";
      },
    },
    {
      key: "operationPayload",
      name: "Payload",
      minWidth: 200,
      maxWidth: 400,
      onRender: (item: IntegrationAuditDto) => {
        const text = item.operationPayload || "-";
        return text.length > 80 ? text.substring(0, 80) + "..." : text;
      },
    },
    {
      key: "actions",
      name: "",
      minWidth: 50,
      maxWidth: 70,
      onRender: (item: IntegrationAuditDto) => (
        <MoreActionsButton
          menu={[
            {
              key: "details",
              text: "Show record audit",
              iconProps: { iconName: "List" },
              onClick: () => {
                setSelectedAudit(item);
                setIsDetailOpen(true);
              },
            },
          ]}
        />
      ),
    },
  ];

  return (
    <Stack tokens={{ childrenGap: 8 }} styles={{ root: { padding: "16px" } }}>
      <Text variant="xLarge" styles={{ root: { fontWeight: 600 } }}>
        Integration Audits
      </Text>

      <Stack horizontal tokens={{ childrenGap: 12 }} verticalAlign="end">
        <Dropdown
          label="Entity Type"
          options={entityTypeOptions}
          selectedKey={entityTypeFilter}
          onChange={(_, option) =>
            setEntityTypeFilter((option?.key as string) ?? "")
          }
          styles={{ dropdown: { width: 250 } }}
        />
        <ComboBox
          label="User"
          options={userOptions}
          selectedKey={userIdFilter ?? null}
          onChange={(_, option) =>
            setUserIdFilter(option ? (option.key as number) : undefined)
          }
          allowFreeform
          autoComplete="on"
          placeholder="Filter by user..."
          styles={{ root: { width: 250 } }}
        />
        <IconButton
          iconProps={{ iconName: "Refresh" }}
          title="Refresh"
          onClick={() => fetchAudits()}
          disabled={loading}
        />
      </Stack>

      {loading ? (
        <ShimmerList numberOfColumns={6} />
      ) : (
        <DataGrid items={filteredAudits} columns={columns} />
      )}

      {selectedAudit && (
        <RigthPanel
          header="Audit Details"
          isOpen={isDetailOpen}
          type={RigthPanelType.large}
          dismissPanel={() => {
            setIsDetailOpen(false);
            setSelectedAudit(null);
          }}
          childComponent={AuditDetailsPanel}
          childProps={{
            audit: selectedAudit,
            onClose: () => {
              setIsDetailOpen(false);
              setSelectedAudit(null);
            },
          }}
        />
      )}
    </Stack>
  );
};

export default IntegrationAuditsPage;
